import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRange } from '../../context/RangeContext/RangeContext';
import { useGame } from '../../context/GameContext';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { QuizFooter, QuizFooterRow } from '../../components/common/QuizFooter';
import RangeMatrix from '../../components/RangeMatrix/RangeMatrix';
import { getRangeFilePath, formatBlastTitle, getBlastPageTitle } from '../BlastConfigScreen/blastRangeRules';
import { BlastPageHeader } from '../../components/blast/BlastPageHeader/BlastPageHeader';

import './blast-range-screen.css';
import '../../components/RangeMatrix/range-matrix.css';

export const BlastRangeScreen = () => {
  const { startNewGame } = useGame();
  const { activeRange, loadRange, isLoading: rangeLoading, isFallback } = useRange();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoaded       = useRef(false);
  const loadedCacheKey  = useRef(null);

  const config             = location.state?.config;
  const isRandom            = location.state?.isRandom ?? false;
  const studyModeWasRandom  = location.state?.studyModeWasRandom ?? false;

  useEffect(() => {
    if (!config) {
      console.error('No config provided');
      setError('Configuration missing');
      setIsLoading(false);
      return;
    }

    const cacheKey = config.primaryAction
      ? `${config.format}_${config.position}_${config.stack}_${config.primaryAction}_${config.secondaryAction ?? ''}_${config.tertiaryAction ?? ''}`
      : `${config.format}_${config.position}_${config.stack}_${config.action}_${config.subtype ?? config.villainAction ?? ''}`;

    // Reset the guard when the config actually changes (e.g. Generate New Range).
    // Keeps the StrictMode double-invoke protection for the same config.
    if (loadedCacheKey.current !== cacheKey) {
      hasLoaded.current = false;
    }

    if (hasLoaded.current) return;
    hasLoaded.current    = true;
    loadedCacheKey.current = cacheKey;

    setIsLoading(true);
    setError(null);

    const loadRangeData = async () => {
      try {
        const rangePath = getRangeFilePath(config);
        if (!rangePath) {
          throw new Error(`No range file found for: ${config.format} ${config.position} ${config.stack}bb ${config.action}`);
        }

        await loadRange({
          position: config.position,
          betType:  cacheKey,
          rangePath
        });
      } catch (error) {
        console.error('Error in loadRangeData:', error);
        setError(error.message);
        hasLoaded.current    = false;
        loadedCacheKey.current = null; // allow retry on error
      } finally {
        setIsLoading(false);
      }
    };

    loadRangeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]); // loadRange and activeRange intentionally excluded — both are unstable references

  // Title delegated to blastRangeRules formatBlastTitle (handles both new and legacy config)
  const formatTitle = formatBlastTitle;

  const handleBeginQuiz = () => {
    if (!config) {
      console.error('No config available for quiz');
      return;
    }

    if (!activeRange || (activeRange instanceof Map && activeRange.size === 0)) {
      setError('Range data is not loaded properly. Please try again.');
      return;
    }

    // filterMode for RangeMatrix — new-style configs set filterMode explicitly (may be null);
    // only fall back to legacy subtype for old-style configs that lack primaryAction.
    const filterMode = config.primaryAction !== undefined
      ? config.filterMode
      : (config.filterMode ?? config.subtype ?? null);

    const gameConfig = {
      ...config,
      position:    config.position,
      totalHands:  config.totalHands || 10,
      quizType:    'BLAST',
      filterMode
    };

    try {
      startNewGame(gameConfig);

      setTimeout(() => {
        navigate('/blast');
      }, 100);
    } catch (error) {
      console.error('Error starting game:', error);
      setError(`Failed to start quiz: ${error.message || 'Unknown error'}`);
    }
  };

  // Sends the user back to the config screen with the exact selection that
  // produced this range, so it can be repopulated for manual tweaking or
  // hitting Reconfigure Quiz again — rather than starting from a blank screen.
  const handleBack = () => {
    if (!config) {
      navigate('/blast-config');
      return;
    }
    navigate('/blast-config', {
      state: {
        restoreConfig: {
          studyMode:       'fixedBlinds',
          format:          config.format,
          position:        config.position,
          stack:           config.stack,
          primaryAction:   config.primaryAction,
          secondaryAction: config.secondaryAction,
          tertiaryAction:  config.tertiaryAction,
          isRandomConfig:  isRandom,
          studyModeWasRandom,
        },
      },
    });
  };

  const loading = isLoading || rangeLoading;

  if (error || (!loading && isFallback)) {
    const errorMessage = error || 'Range data is not available for this scenario yet. Please select a different configuration.';
    return (
      <div className="blast-screen">
        <div className="blast-screen-content">
          <BlastPageHeader
            leftLink={{ label: 'Dashboard', onClick: () => navigate('/') }}
            title={getBlastPageTitle(config?.format)}
          />
          <div className="error-message" style={{ color: 'red', padding: '20px' }}>
            {errorMessage}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blast-screen">
      <div className="blast-screen-content">
        <BlastPageHeader
          leftLink={{ label: 'Dashboard', onClick: () => navigate('/') }}
          title={getBlastPageTitle(config?.format)}
        />
        <div className="range-info">
          <h2 className="range-subtitle">
            {formatTitle(config)}
            {config?.stack && (
              <span className="range-stack-tag"> {config.stack}bb</span>
            )}
          </h2>
          <p className="range-description">
            This is the range you'll be using. When you're ready, press Begin Quiz.
          </p>
        </div>

        {loading ? (
          <div className="loading-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            width: '100%',
            fontSize: '16px',
            color: '#3E3E45'
          }}>
            Loading range...
          </div>
        ) : (
          <>
            <div className="range-matrix-fill">
              <RangeMatrix
                activeRange={activeRange}
                filterMode={config?.primaryAction !== undefined
                  ? config.filterMode
                  : (config?.filterMode ?? config?.subtype ?? null)}
                size="fill"
              />
            </div>

            <QuizFooter horizontalPadding={16} rowGap={4}>
              <QuizFooterRow gap={4}>
                <ButtonLarge
                  buttonLargeClassName="button-large go-back-button"
                  onClick={handleBack}
                  text="Back"
                />
                <ButtonLarge
                  buttonLargeClassName="button-large begin-quiz-button-half"
                  onClick={handleBeginQuiz}
                  text="Begin Quiz"
                />
              </QuizFooterRow>
            </QuizFooter>
          </>
        )}
      </div>
    </div>
  );
};

export default BlastRangeScreen;
